import { MemoryStorage, OAuthStorage } from 'angular-oauth2-oidc';

/**
 * The keys angular-oauth2-oidc uses for the *in-flight* login only: the nonce and
 * PKCE verifier a single authorization code is bound to, plus the route to restore
 * afterwards.
 *
 * They must never live in a storage shared between tabs. A second login flow — a
 * second tab, or a second `initCodeFlow()` inside the same page load — overwrites
 * them, and the authorization code that comes back then belongs to the *other*
 * flow's verifier. `tryLogin()` rejects with `invalid_nonce_in_state`, the code is
 * never redeemed, and the app dead-ends on a blank page.
 *
 * Tokens stay in localStorage, so sessions still survive new tabs and browser
 * restarts.
 */
export const TRANSIENT_LOGIN_KEYS: ReadonlySet<string> = new Set([
  'nonce',
  'PKCE_verifier',
  'session_state',
  'requested_route',
]);

function hasWebStorage(): boolean {
  return typeof localStorage !== 'undefined' && typeof sessionStorage !== 'undefined';
}

/**
 * Splits the OAuth storage: transient login state per tab (sessionStorage, which
 * survives the redirect to the identity provider and back), tokens shared across
 * tabs (localStorage).
 */
export function createOAuthStorage(): OAuthStorage {
  if (!hasWebStorage()) {
    return new MemoryStorage();
  }

  const storageFor = (key: string): Storage => (TRANSIENT_LOGIN_KEYS.has(key) ? sessionStorage : localStorage);

  return {
    getItem: (key: string): string | null => storageFor(key).getItem(key),
    setItem: (key: string, data: string): void => storageFor(key).setItem(key, data),
    // Removal clears both: before this split the transient keys were written to
    // localStorage, and a leftover `nonce` there would break the next login.
    removeItem: (key: string): void => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    },
  };
}

/**
 * Drops the half-finished login state so the next `initCodeFlow()` starts from a
 * clean slate. Leaves any tokens alone.
 */
export function clearTransientLoginKeys(): void {
  if (!hasWebStorage()) {
    return;
  }

  TRANSIENT_LOGIN_KEYS.forEach((key: string) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });
}
